import {
  Alert,
  AlertIcon,
  Box,
  Button,
  ButtonGroup,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  type UseModalProps,
} from "@chakra-ui/react";
import { FormRow, Select } from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import {
  type MatchedPositionsState,
  MoveHoursToProjectStore,
} from "@src/components/modules/time-tracking/Report/MoveHoursToProjectStore";
import { CancelButton } from "@src/components/ui-kit/Button/CancelButton";
import { Form } from "@src/components/ui-kit/Form";
import { Icon } from "@src/components/ui-kit/Icon";
import { fieldToSelectProps } from "@src/utils/forms/inputHelpers";
import { useStore } from "@src/utils/hooks";
import mapToOptions from "@src/utils/map-to-options";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import type { Store } from "./Store";

interface MoveHoursToProjectModalProps extends UseModalProps {
  selectedIds: string[];
  clientsSimpleMap: NonNullable<Store["optionsData"]>["clientsSimpleMap"];
  onMoveCompleted: () => void;
}

const MoveHoursToProjectModal = ({
  selectedIds,
  clientsSimpleMap,
  onMoveCompleted,
  isOpen,
  onClose,
}: MoveHoursToProjectModalProps) => {
  const matchedPositionsText: Record<MatchedPositionsState, string> = {
    all: t`All positions are matched. You can move selected hours`,
    none: t`Match listed position to positions where I should move selected hours`,
    some: t`I matched some positions. But I could not match some of them. Match highlighted ones.`,
  };

  const appStore = useStore();
  const [store] = useState(() => new MoveHoursToProjectStore(appStore));

  const onSubmit = async () => {
    const isSuccess = await store.moveItems();
    if (!isSuccess) return;
    _onClose();
    onMoveCompleted();
  };

  const title = t`Move ${appStore.workspaceStore
    .settings!.timeTrackingRateTypeReadable()
    .toLowerCase()} to another project`;

  const onBrandSelect = async (value: string) => {
    store.brand_id.onChange(value);
    store.project_id.onChange(undefined);
    await store.getProjectOptions();
  };

  const onProjectSelect = async (value: string) => {
    store.project_id.onChange(value);
    store.task_id.onChange(undefined);
    store.budget_item_id.onChange(undefined);
    store.resolvePositions();
    await store.getBudgetItemOptions();
  };

  const onBudgetItemSelect = async (value: string) => {
    store.budget_item_id.onChange(value);
    store.task_id.onChange(undefined);
    await store.getTaskOptions();
  };

  const _onClose = () => {
    store.form.reset();
    store.setSelectedIds([]);
    store.distinctWorkTypes = [];
    onClose();
  };

  useEffect(() => {
    store.setSelectedIds(selectedIds);
    store.brandOptions = mapToOptions.clientBrands(clientsSimpleMap ?? []);
  }, [selectedIds, clientsSimpleMap]);

  useEffect(() => {
    if (!isOpen) return;
    store.fetchDistinctWorkTypes();
  }, [isOpen, selectedIds]);

  return (
    <Modal isOpen={isOpen} onClose={_onClose} size="2xl" trapFocus={false}>
      <ModalOverlay />
      <Form onSubmit={onSubmit}>
        <ModalContent>
          <ModalHeader>{title}</ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <FormRow title={t`Brand`}>
              <Select
                asPortal
                placeholder={t`Select brand`}
                options={store.brandOptions}
                value={store.brand_id.value}
                onChange={onBrandSelect}
              />
            </FormRow>
            <FormRow title={t`Project`}>
              <Select
                asPortal
                placeholder={t`Select project`}
                isLoading={store.isProjectOptionsLoading}
                options={store.projectOptions}
                value={store.project_id.value}
                onChange={onProjectSelect}
                isDisabled={!store.brand_id.value}
              />
            </FormRow>
            <FormRow title={t`Budget item`}>
              <Select
                asPortal
                placeholder={t`Select budget item`}
                isLoading={store.isBudgetItemOptionsLoading}
                options={store.budgetItemOptions}
                value={store.budget_item_id.value}
                onChange={onBudgetItemSelect}
                isDisabled={!store.project_id.value}
              />
            </FormRow>
            <FormRow title={t`Task`}>
              <Select
                asPortal
                placeholder={t`Select task`}
                isLoading={store.isTaskOptionsLoading}
                {...fieldToSelectProps(store.task_id, store.taskOptions)}
                isDisabled={!store.budget_item_id.value}
              />
            </FormRow>

            {!!store.matchedPositionsState && (
              <Stack mt="6" spacing="3">
                <Alert rounded="lg" status="info">
                  <AlertIcon />
                  {matchedPositionsText[store.matchedPositionsState]}
                </Alert>
                <Box>
                  {store.time_tracking_work_type_mapping?.$.map(
                    (item, index) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: Item has no unique identifier
                      <HStack key={index} mb="2" spacing="3">
                        <Select
                          asPortal
                          isDisabled
                          {...fieldToSelectProps(
                            item.$.from_time_tracking_work_type_id,
                            store.missingPositions,
                          )}
                        />
                        <Icon name="arrow-narrow-right" color="grey.400" />
                        <Select
                          asPortal
                          {...fieldToSelectProps(
                            item.$.to_time_tracking_work_type_id,
                            store.selectedProjectPositionOptions,
                          )}
                        />
                      </HStack>
                    ),
                  )}
                </Box>
              </Stack>
            )}
          </ModalBody>

          <ModalFooter>
            <ButtonGroup spacing="3">
              <CancelButton onClick={_onClose} />
              <Button
                isDisabled={store.form.hasError}
                isLoading={store.isMoveLoading}
                type="submit"
              >
                <Trans>Save</Trans>
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Form>
    </Modal>
  );
};

export default observer(MoveHoursToProjectModal);
